import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ButtonVariant } from '@innovamat/glimmer-components';
import { Button, Dropdown } from '@innovamat/glimmer-components';
import type { ClassroomBody, GlowApiError } from '@innovamat/glow-api-client';

import { useUser } from '../../../user-management';
import { addClassroomToCache } from '../../cache-actions';
import { useClassroom } from '../../hooks/use-classroom';
import { CLASSROOM_TYPE, type ClassroomType } from '../../modules/classroom';
import { AddClassroomModal } from '../add-classroom-modal';

const ClassroomDropdownOptions = {
  ADD: 'add',
  IMPORT: 'import',
  JOIN: 'join',
  ADD_JOURNEY: 'add-journey',
  JOIN_JOURNEY: 'join-journey',
} as const;

type AddClassroomDropdownOption =
  (typeof ClassroomDropdownOptions)[keyof typeof ClassroomDropdownOptions];

type AddClassroomDropdownProps = {
  disabled?: boolean;
  options: AddClassroomDropdownOption[];
  position?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
  size?: 'S' | 'M';
  variant?: ButtonVariant;
  onError?: (error: GlowApiError) => void;
  onJoinClassroom?: () => void;
  onJoinJourney?: () => void;
  onSuccess?: (classroom: ClassroomBody) => void;
  setEvent?: (event: AssignEventType) => void;
};

export type AssignEventType = 'new' | 'import' | 'existing';

export function AddClassroomDropdown({
  disabled,
  options,
  position = 'bottomRight',
  size = 'S',
  variant = 'secondary',
  onError,
  onJoinClassroom,
  onJoinJourney,
  onSuccess,
  setEvent,
}: AddClassroomDropdownProps): JSX.Element | null {
  const { user } = useUser();
  const organizationId = user?.organizationId;

  const [showCreateClassroom, setShowCreateClassroom] = useState(false);
  const [classroomType, setClassroomType] = useState<ClassroomType>(
    CLASSROOM_TYPE.CLASSROOM
  );
  const [classroomId, setClassroomId] = useState<string | undefined>(undefined);
  const processedClassroomIds = useRef(new Set<string>());

  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { classroom } = useClassroom({ classroomId: classroomId });

  const handleOnSuccess = async (
    addedClassroom: ClassroomBody
  ): Promise<void> => {
    setShowCreateClassroom(false);
    setClassroomId(addedClassroom.id);
    onSuccess?.(addedClassroom);
  };

  const handleSelectItemEvent = (type: AssignEventType): void => {
    setEvent?.(type);
  };

  const handleAddGroup = (): void => {
    setShowCreateClassroom(true);
    setClassroomType(CLASSROOM_TYPE.JOURNEYS);
    handleSelectItemEvent('new');
  };

  const handleAdd = (): void => {
    setShowCreateClassroom(true);
    handleSelectItemEvent('new');
  };

  const handleJoin = (): void => {
    onJoinClassroom?.();
    handleSelectItemEvent('existing');
  };

  const handleJoinJourney = (): void => {
    onJoinJourney?.();
    handleSelectItemEvent('existing');
  };

  useEffect(() => {
    if (
      classroom &&
      !processedClassroomIds.current.has(classroom.id) &&
      organizationId
    ) {
      addClassroomToCache({
        queryClient,
        orgId: organizationId,
        classroom,
      });
      processedClassroomIds.current.add(classroom.id);
    }
  }, [classroom]);

  const isJourney = options.some((option) => option.includes('journey'));

  if (options.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown closeOnSelectItem>
        <Dropdown.Toggle>
          <Button
            size={size}
            variant={variant}
            rightIcon="ExpandMoreIcon"
            disabled={disabled}
            oneLine
            dataTestId="add-classroom-dropdown-button"
          >
            {isJourney
              ? t('classroom.title.createGroup')
              : t('classrooms.addClassroom')}
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Content position={position}>
          {options.includes(ClassroomDropdownOptions.JOIN) &&
            onJoinClassroom && (
              <Dropdown.Item
                dataTestId="add-classroom-dropdown-join-classroom"
                state={'active'}
                icon="JoinClassIcon"
                onSelectItem={handleJoin}
              >
                {t('classrooms.join-to-classroom')}
              </Dropdown.Item>
            )}

          {options.includes(ClassroomDropdownOptions.ADD) && (
            <Dropdown.Item
              dataTestId="add-classroom-dropdown-create-new-classroom"
              state={'active'}
              icon="AddUpIcon"
              onSelectItem={handleAdd}
            >
              {t('classrooms.dropdown.createNewClassroom')}
            </Dropdown.Item>
          )}

          {options.includes(ClassroomDropdownOptions.ADD_JOURNEY) && (
            <Dropdown.Item
              state={'active'}
              icon="AddUpIcon"
              onSelectItem={handleAddGroup}
            >
              {t('classroom.title.createGroup')}
            </Dropdown.Item>
          )}

          {options.includes(ClassroomDropdownOptions.JOIN_JOURNEY) && (
            <Dropdown.Item
              state={'active'}
              icon="JoinClassIcon"
              onSelectItem={handleJoinJourney}
            >
              {t('classrooms.join-to-journey')}
            </Dropdown.Item>
          )}
        </Dropdown.Content>
      </Dropdown>

      <AddClassroomModal
        show={showCreateClassroom}
        onClose={() => setShowCreateClassroom(false)}
        onSuccess={handleOnSuccess}
        onError={onError}
        classroomType={classroomType}
      />
    </>
  );
}
export { ClassroomDropdownOptions };
export type { AddClassroomDropdownOption };
